import { Wave } from '../../wave/types/wave.type';
import { getWaveLeads, isWaveOfType } from '../../wave/utils/wave.util';

export enum Noise {
  clean,
  ok,
  disabling,
}

interface Segment {
  start: number;
  end: number;
  type: Noise;
}

interface Point {
  type: 'start' | 'end';
  wave: Wave;
  ts: number;
}

type LeadCounter = Record<string /* lead name */, number>;

const buildLeadCounter = (leads: string[]): LeadCounter =>
  leads.reduce((counter: LeadCounter, lead) => {
    counter[lead] = 0;
    return counter;
  }, {});

export const getNoiseSegments = (waves: Wave[], leads: string[]): Segment[] => {
  const starts: Point[] = waves.map((wave) => ({
    type: 'start',
    ts: wave.onset,
    wave,
  }));
  const ends: Point[] = waves.map((wave) => ({
    type: 'end',
    ts: wave.offset,
    wave,
  }));

  const points = starts.concat(ends).sort((a, b) => a.ts - b.ts);

  const nonDisabling = buildLeadCounter(leads);
  const disabling = buildLeadCounter(leads);

  const isDisabling = () =>
    Object.values(disabling).every((count) => count > 0);
  const isNonDisabling = () =>
    Object.values(disabling).some((count) => count > 0) ||
    Object.values(nonDisabling).some((count) => count > 0);

  const getCurrentType = () => {
    return isDisabling()
      ? Noise.disabling
      : isNonDisabling()
      ? Noise.ok
      : Noise.clean;
  };

  const segments: Segment[] = [{ start: 0, end: 0, type: getCurrentType() }];

  for (const point of points) {
    const lastSegment = segments[segments.length - 1];
    const start = lastSegment.end;
    const end = point.ts;

    const type = getCurrentType();

    if (type === lastSegment.type) {
      lastSegment.end = end;
    } else {
      segments.push({ start, end, type });
    }

    const increment = point.type === 'start' ? +1 : -1;

    if (isWaveOfType(point.wave, 'artifact')) {
      const currentLeads = getWaveLeads(point.wave, leads);
      const actualLeads = currentLeads.length > 0 ? currentLeads : leads;
      actualLeads.forEach((lead) => (nonDisabling[lead] += increment));
    }
    if (
      isWaveOfType(
        point.wave,
        'noise',
        'calibration',
        'no_signal',
        'p_level_noise',
        'qrs_level_noise',
        'pt_level_noise',
        'uncertain',
        'microvolted',
      )
    ) {
      const currentLeads = getWaveLeads(point.wave, leads);
      const actualLeads = currentLeads.length > 0 ? currentLeads : leads;
      actualLeads.forEach((lead) => (disabling[lead] += increment));
    }
  }

  return segments;
};
